import { create } from 'zustand';
import { announce } from '../components/ui/LiveAnnouncer';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  action?: { label: string; onClick: () => void };
  /** Auto-dismiss after this many ms (see `effectiveToastDuration`). */
  duration?: number;
  /** FIX-NOTIF N-5: an icon of its own instead of the type's (a notification toast shows its kind). */
  icon?: React.ElementType;
  /** FIX-NOTIF N-5: a person's name - the toast shows their avatar circle (a reply) instead of an icon. */
  avatar?: string;
}

/**
 * Toast v2 (PRD §4.8, T-11) + D-153 + D-285: EVERY toast closes by itself — every
 * type after 4 s (a caller with a button to press may ask for longer, up to 10 s); the ✕ button stays. Nothing
 * is sticky any more (an error used to stay until closed, e.g. "כתובת אימייל
 * כבר בשימוש"). Timers pause while the pointer or focus is on the toast. At
 * most 3 are visible. Every toast is also read by the LiveAnnouncer (polite;
 * errors assertive).
 */
export const TOAST_DURATIONS: Record<ToastType, number> = {
  success: 4000,
  info: 4000,
  warning: 4000,
  error: 4000,
};

/**
 * D-153 — the longest a caller may ask for (the few warnings that must be read,
 * e.g. "המייל לא נשלח", ask for 10 s). Anything longer, 0, negative or not a
 * finite number falls back to / is capped at a closing toast — never sticky.
 */
export const MAX_TOAST_DURATION_MS = 10_000;

export function effectiveToastDuration(type: ToastType, requested?: number): number {
  if (requested === undefined || !Number.isFinite(requested) || requested <= 0) return TOAST_DURATIONS[type];
  return Math.min(requested, MAX_TOAST_DURATION_MS);
}

export const MAX_VISIBLE_TOASTS = 3;

interface ToastState {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  /** Stop the auto-dismiss clock (hover / focus). */
  pauseToast: (id: string) => void;
  /** Restart the clock with the time that was left. */
  resumeToast: (id: string) => void;
}

let counter = 0;

/** Per-toast auto-dismiss clocks, kept outside the React state. */
const clocks = new Map<string, { timer?: ReturnType<typeof setTimeout>; remaining: number; startedAt: number }>();

function stopClock(id: string) {
  const clock = clocks.get(id);
  if (clock?.timer) clearTimeout(clock.timer);
  clocks.delete(id);
}

export const useToastStore = create<ToastState>((set, get) => {
  const startClock = (id: string, ms: number) => {
    const timer = setTimeout(() => get().removeToast(id), ms);
    clocks.set(id, { timer, remaining: ms, startedAt: Date.now() });
  };

  return {
    toasts: [],
    addToast: (toast) => {
      const id = `toast-${++counter}-${Date.now()}`;
      const duration = effectiveToastDuration(toast.type, toast.duration);
      const kept = get().toasts.slice(-(MAX_VISIBLE_TOASTS - 1));
      for (const old of get().toasts) if (!kept.includes(old)) stopClock(old.id);
      set({ toasts: [...kept, { ...toast, duration, id }] });
      if (duration > 0) startClock(id, duration);
      announce(toast.message, toast.type === 'error' ? 'assertive' : 'polite');
      return id;
    },
    removeToast: (id) => {
      stopClock(id);
      set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
    },
    pauseToast: (id) => {
      const clock = clocks.get(id);
      if (!clock?.timer) return;
      clearTimeout(clock.timer);
      clocks.set(id, { remaining: Math.max(0, clock.remaining - (Date.now() - clock.startedAt)), startedAt: 0 });
    },
    resumeToast: (id) => {
      const clock = clocks.get(id);
      if (!clock || clock.timer) return;
      startClock(id, Math.max(1000, clock.remaining));
    },
  };
});

/** Show a toast. Returns its id (for `useToastStore.getState().removeToast`). */
export function toast(type: ToastType, message: string, options?: { action?: Toast['action']; duration?: number; icon?: Toast['icon']; avatar?: string }): string {
  return useToastStore.getState().addToast({ type, message, ...options });
}
